export type LoginInput = {
    email: string;
    password: string;
};

export type LoginCheckInput = {
    email: string;
    code: string;
};

export type ForgotPasswordSubmitInput = {
    email: string;
    code: string;
    newPassword: string;
};

export type ChallengeName = 'CUSTOM_CHALLENGE' | 'NEW_PASSWORD_REQUIRED' | 'EMAIL_OTP' | 'TOTP_CODE';

export interface MfaSettings {
    email: boolean;
    totp: boolean;
}

export interface TotpSetupDetails {
    /** Secret a saisir manuellement dans l'application d'authentification */
    secret: string;
    /** URI otpauth:// a encoder en QR code */
    uri: string;
}

export interface AuthenticationStore {
    isInitialized: boolean;
    hasSession: boolean;
    /**
     * Part vers le fournisseur d'identite : la page est REMPLACEE, donc rien ne s'execute
     * apres. Le retour arrive sur `/login`, ou l'echange du code se fait tout seul.
     *
     * Attention, ELLE LEVE QUAND L'OAUTH N'EST PAS CONFIGURE, plutot que de ne rien faire :
     * un bouton qui ne repond pas se lit comme une panne du reseau, et l'appelant doit
     * pouvoir le dire. Voir `awsOauthDomain` sur le provider.
     */
    signInWithSso: (providerName: string) => Promise<void>;
    /**
     * La session en cours vient-elle d'un fournisseur d'identite externe ?
     *
     * Attention, C'EST UN FAIT DE LA SESSION ET NON DE LA PERSONNE. La meme personne qui se
     * reconnecte avec son mot de passe rend `false`, et c'est voulu : ce qui dispense du
     * second facteur est celui qu'Entra ID a deja demande sur CE chemin-la.
     *
     * `false` quand rien ne repond : une lecture ratee ne doit pas faire sauter une barriere.
     */
    isFederatedSession: () => Promise<boolean>;
    logout: () => Promise<void>;
    getAuthorizationToken: () => Promise<string | null>;
    login: (args: LoginInput) => Promise<ChallengeName | undefined>;
    passwordLessLogin: (email: string) => Promise<void>;
    passwordLessLoginCheck: (args: LoginCheckInput) => Promise<void>;
    completeNewPassword: (password: string) => Promise<void>;
    /** Valide le code MFA recu par email (challenge EMAIL_OTP au login) */
    confirmMfaCode: (code: string) => Promise<void>;
    /** La double authentification par email est-elle activee pour l'utilisateur connecte ? */
    fetchEmailMfaEnabled: () => Promise<boolean>;
    /** Methodes MFA actives pour l'utilisateur connecte */
    fetchMfaSettings: () => Promise<MfaSettings>;
    /** Active/desactive la double authentification par email (MFA optionnel, choix utilisateur) */
    setEmailMfaEnabled: (enabled: boolean) => Promise<void>;
    /** Demarre l'enrolement TOTP : renvoie le secret et l'URI a scanner */
    setupTotp: (accountName: string) => Promise<TotpSetupDetails>;
    /** Valide le code de l'application d'authentification et active le TOTP en methode preferee */
    verifyTotpSetup: (code: string) => Promise<void>;
    /** Desactive le TOTP */
    disableTotp: () => Promise<void>;
    forgotPassword: (username: string) => Promise<void>;
    forgotPasswordSubmit: (args: ForgotPasswordSubmitInput) => Promise<void>;
}

export enum AuthStep {
    LOGIN,
    /**
     * L'adresse seule, pour trouver le fournisseur d'identite de l'entreprise (PCG-304).
     *
     * Attention, C'EST UN ETAT DU MEME ECRAN ET NON UNE ROUTE. Une route rendrait l'etape
     * partageable par URL et amputee de son contexte, alors qu'elle n'est que le formulaire
     * de connexion sous un autre visage, avec l'adresse deja saisie.
     */
    SSO_EMAIL,
    NEW_PASSWORD,
    MFA_CODE,
    DONE,
}
